/**
 * Lazy-loaded Page Components
 * Route-based code splitting with optimized loading
 */

import { createLazyComponent } from '../../utils/performance';
import { withLazyLoading, LoadingFallback } from './LazyComponents';

// Lazy-loaded page components with chunk names for better debugging
export const LazyDashboard = createLazyComponent(
  () => import('../../pages/Dashboard'),
  {
    chunkName: 'dashboard',
    preload: false // Dashboard will be loaded on demand
  }
);

export const LazyReports = createLazyComponent(
  () => import('../../pages/Reports'),
  {
    chunkName: 'reports',
    preload: false // Heavy component with charts
  }
);

export const LazyAuditLog = createLazyComponent(
  () => import('../../pages/AuditLog'),
  {
    chunkName: 'audit-log',
    preload: false // Heavy component with tables
  }
);

export const LazyChat = createLazyComponent(
  () => import('../../pages/Chat'),
  {
    chunkName: 'chat',
    preload: false // Future phase component
  }
);

export const LazySettings = createLazyComponent(
  () => import('../../pages/Settings'),
  {
    chunkName: 'settings',
    preload: false // Settings page
  }
);

// Auth pages - can be preloaded since they're likely to be accessed together
export const LazyLogin = createLazyComponent(
  () => import('../../pages/auth/Login'),
  {
    chunkName: 'auth-login',
    preload: false
  }
);

export const LazySignup = createLazyComponent(
  () => import('../../pages/auth/Signup'),
  {
    chunkName: 'auth-signup',
    preload: false
  }
);

export const LazyVerifyEmail = createLazyComponent(
  () => import('../../pages/auth/VerifyEmail'),
  {
    chunkName: 'auth-verify-email',
    preload: false
  }
);

export const LazyVerificationSent = createLazyComponent(
  () => import('../../pages/auth/VerificationSent'),
  {
    chunkName: 'auth-verification-sent',
    preload: false
  }
);

export const LazyMagicLinkSent = createLazyComponent(
  () => import('../../pages/auth/MagicLinkSent'),
  {
    chunkName: 'auth-magic-link-sent',
    preload: false
  }
);

// Enhanced lazy page components with proper loading states
export const DashboardWithSuspense = withLazyLoading(LazyDashboard, {
  componentName: 'Dashboard',
  loadingType: 'page',
  showProgress: true
});

export const ReportsWithSuspense = withLazyLoading(LazyReports, {
  componentName: 'Reports',
  loadingType: 'page',
  showProgress: true
});

export const AuditLogWithSuspense = withLazyLoading(LazyAuditLog, {
  componentName: 'Audit Log',
  loadingType: 'table',
  showProgress: true
});

export const ChatWithSuspense = withLazyLoading(LazyChat, {
  componentName: 'Chat',
  loadingType: 'component',
  showProgress: false
});

export const SettingsWithSuspense = withLazyLoading(LazySettings, {
  componentName: 'Settings',
  loadingType: 'page',
  showProgress: false
});

export const LoginWithSuspense = withLazyLoading(LazyLogin, {
  componentName: 'Login',
  loadingType: 'component',
  showProgress: false
});

export const SignupWithSuspense = withLazyLoading(LazySignup, {
  componentName: 'Signup',
  loadingType: 'component',
  showProgress: false
});

export const VerifyEmailWithSuspense = withLazyLoading(LazyVerifyEmail, {
  componentName: 'Verify Email',
  loadingType: 'component',
  showProgress: false
});

export const VerificationSentWithSuspense = withLazyLoading(LazyVerificationSent, {
  componentName: 'Verification Sent',
  loadingType: 'component',
  showProgress: false
});

export const MagicLinkSentWithSuspense = withLazyLoading(LazyMagicLinkSent, {
  componentName: 'Magic Link Sent',
  loadingType: 'component',
  showProgress: false
});

// Preloading utilities
export const preloadCriticalRoutes = () => {
  // Preload dashboard after a delay since it's the most common destination
  setTimeout(() => {
    import('../../pages/Dashboard').catch(console.error);
    console.log('🔄 Preloaded Dashboard component');
  }, 2000);
};

export const preloadReportsRoute = () => {
  // Preload reports when user might navigate there
  import('../../pages/Reports').catch(console.error);
  console.log('🔄 Preloaded Reports component');
};

export const preloadAuditRoute = () => {
  // Preload audit log when user might navigate there
  import('../../pages/AuditLog').catch(console.error);
  console.log('🔄 Preloaded Audit Log component');
};

// Lazy load heavy chart components
export const LazyProfitLossChart = createLazyComponent(
  () => import('../reports/charts/ProfitLossChart').then(m => ({ default: m.ProfitLossChart })),
  {
    chunkName: 'chart-profit-loss',
    preload: false
  }
);

export const LazyBalanceSheetChart = createLazyComponent(
  () => import('../reports/charts/BalanceSheetChart').then(m => ({ default: m.BalanceSheetChart })),
  {
    chunkName: 'chart-balance-sheet',
    preload: false
  }
);

export const LazyCashFlowChart = createLazyComponent(
  () => import('../reports/charts/CashFlowChart').then(m => ({ default: m.CashFlowChart })),
  {
    chunkName: 'chart-cash-flow',
    preload: false
  }
);

export const LazySalesDashboardChart = createLazyComponent(
  () => import('../reports/charts/SalesDashboardChart').then(m => ({ default: m.SalesDashboardChart })),
  {
    chunkName: 'chart-sales-dashboard',
    preload: false
  }
);

export const LazyInventoryChart = createLazyComponent(
  () => import('../reports/charts/InventoryChart').then(m => ({ default: m.InventoryChart })),
  {
    chunkName: 'chart-inventory',
    preload: false
  }
);

export const LazyCustomerOutstandingChart = createLazyComponent(
  () => import('../reports/charts/CustomerOutstandingChart').then(m => ({ default: m.CustomerOutstandingChart })),
  {
    chunkName: 'chart-customer-outstanding',
    preload: false
  }
);

export const LazyVendorPaymentsChart = createLazyComponent(
  () => import('../reports/charts/VendorPaymentsChart').then(m => ({ default: m.VendorPaymentsChart })),
  {
    chunkName: 'chart-vendor-payments',
    preload: false
  }
);

// Enhanced chart components with loading states
export const ProfitLossChartWithSuspense = withLazyLoading(LazyProfitLossChart, {
  componentName: 'Profit & Loss Chart',
  loadingType: 'chart',
  showProgress: true
});

export const BalanceSheetChartWithSuspense = withLazyLoading(LazyBalanceSheetChart, {
  componentName: 'Balance Sheet Chart',
  loadingType: 'chart',
  showProgress: true
});

export const CashFlowChartWithSuspense = withLazyLoading(LazyCashFlowChart, {
  componentName: 'Cash Flow Chart',
  loadingType: 'chart',
  showProgress: true
});

export const SalesDashboardChartWithSuspense = withLazyLoading(LazySalesDashboardChart, {
  componentName: 'Sales Dashboard Chart',
  loadingType: 'chart',
  showProgress: true
});

export const InventoryChartWithSuspense = withLazyLoading(LazyInventoryChart, {
  componentName: 'Inventory Chart',
  loadingType: 'chart',
  showProgress: true
});

export const CustomerOutstandingChartWithSuspense = withLazyLoading(LazyCustomerOutstandingChart, {
  componentName: 'Customer Outstanding Chart',
  loadingType: 'chart',
  showProgress: true
});

export const VendorPaymentsChartWithSuspense = withLazyLoading(LazyVendorPaymentsChart, {
  componentName: 'Vendor Payments Chart',
  loadingType: 'chart',
  showProgress: true
});

// Bundle preloading based on user behavior
export const smartPreloader = {
  // Preload based on current route
  preloadForRoute: (currentRoute: string) => {
    switch (currentRoute) {
      case '/dashboard':
        // From dashboard, users often go to reports
        setTimeout(() => preloadReportsRoute(), 3000);
        break;
      case '/reports':
        // From reports, users might check audit logs
        setTimeout(() => preloadAuditRoute(), 2000);
        break;
      default:
        // Default preloading
        setTimeout(() => preloadCriticalRoutes(), 1000);
    }
  },

  // Preload on user interaction hints
  preloadOnHover: (targetRoute: string) => {
    switch (targetRoute) {
      case 'reports':
        preloadReportsRoute();
        break;
      case 'audit':
        preloadAuditRoute();
        break;
      case 'dashboard':
        import('../../pages/Dashboard').catch(console.error);
        break;
    }
  },

  // Preload charts when reports page is accessed
  preloadChartsForReports: () => {
    setTimeout(() => {
      import('../reports/charts/ProfitLossChart').catch(console.error);
      import('../reports/charts/SalesDashboardChart').catch(console.error);
    }, 1000);
    
    setTimeout(() => {
      import('../reports/charts/BalanceSheetChart').catch(console.error);
      import('../reports/charts/CashFlowChart').catch(console.error);
    }, 2000);
  }
};